// Updated SubmissionDocumentsTab.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import {
  FileIcon,
  Paperclip,
  Download,
  FileText,
  Plus,
  Trash2,
} from "lucide-react";
import { format, isValid } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
  SubmissionResponseDto,
  GetProjectResponseDto,
  IAHJDocument,
} from "@/dto/get-project-response.dto";
import { projectApi } from "@/services/projectService";
import { Loader2 } from "lucide-react";
import { AddAdditionalDocumentModal } from "@/components/projects/AddAdditionalDocumentModal";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { Roles } from "@/enums/roles.enum";
import { useDeviceType } from "@/hooks/use-mobile";

interface SubmissionDocumentsTabProps {
  projectId: string;
  submission?: SubmissionResponseDto | null;
  project?: GetProjectResponseDto;
  onRefresh?: () => void;
}

const documentTypeLabels: Record<string, string> = {
  commodityLetter: "Commodity Letter",
  sprinklerInfo: "Sprinkler Info",
  cadFile: "CAD File",
  projectValuationDocs: "Project Valuation",
  po: "Purchase Order",
  bom: "Bill of Materials",
  contractorLicense: "Contractor License",
  architecturalFile: "Architectural File",
  floorPlanElevations: "Floor Plan & Elevations",
};

const permittingRequirementsLabels: Record<string, string> = {
  slabUpliftTest: "Slab Uplift Test",
  slabCoreTest: "Slab Core Test",
  adaPlans: "ADA Plans",
  photometricPlans: "Photometric Plans",
  egressPlans: "Egress Plans",
  smokeVentInspections: "Smoke Vent Inspections",
  fpeStamp: "FPE Stamp",
  weightSigns: "Weight Signs",
  flueKeepersPalletStops: "Flue Keepers Pallet Stops",
  keepClearsStickers: "Keep Clears Stickers",
  fireSignsCommoditySigns: "Fire Signs/Commodity Signs",
};

const fileTypeIcons: Record<string, React.ReactNode> = {
  pdf: <FileText className="h-5 w-5" />,
  docx: <FileText className="h-5 w-5" />,
  xlsx: <FileText className="h-5 w-5" />,
  dwg: <FileText className="h-5 w-5" />,
  jpg: <FileText className="h-5 w-5" />,
  png: <FileText className="h-5 w-5" />,
  default: <FileIcon className="h-5 w-5" />,
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
  return `${size} ${sizes[i]}`;
};

const getFileIcon = (fileName: string) => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "default";
  return fileTypeIcons[extension] || fileTypeIcons.default;
};

const SubmissionDocumentsTab = ({
  projectId,
  submission,
  project,
  onRefresh,
}: SubmissionDocumentsTabProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { isMobile } = useDeviceType();
  const [downloading, setDownloading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState<string | null>(null);

  // Members can only view, no actions
  const canPerformActions =
    project &&
    user?.role !== Roles.MEMBER &&
    (user?.role === Roles.ADMIN ||
      user?.role === Roles.CONTRACTOR ||
      user?.role === Roles.CLIENT ||
      user?._id === project.createdBy._id);

  // Helper function to safely get user name
  const getUserName = (
    user: { firstName?: string; lastName?: string } | string | null | undefined
  ) => {
    // If user is just a string (ID), return Unknown User
    if (typeof user === "string") {
      return "Unknown User";
    }

    if (!user || !user.firstName || !user.lastName) {
      return "Unknown User";
    }
    return `${user.firstName} ${user.lastName}`.trim();
  };

  const formatDate = (date: Date | string) => {
    const parsedDate = new Date(date);
    if (!isValid(parsedDate)) return "Invalid date";

    return format(parsedDate, "MMM d, yyyy 'at' h:mm a");
  };

  const cleanedFiles = submission?.files
    ? Object.fromEntries(
        Object.entries(submission.files).filter(([key]) => key !== "_id")
      )
    : {};

  const documents: Array<{
    name: string;
    url?: string;
    size: number;
    typeLabel: string;
    key: string;
    uploadedAt: Date | string;
    uploadedBy: string;
    source: "submission";
  }> = [];

  // Process each file type, handling arrays for sprinklerInfo, architecturalFile, and floorPlanElevations
  Object.entries(cleanedFiles).forEach(([key, doc]) => {
    if (
      key === "sprinklerInfo" ||
      key === "architecturalFile" ||
      key === "floorPlanElevations"
    ) {
      // Handle multiple files
      const fileArray = Array.isArray(doc) ? doc : [doc];
      fileArray.forEach((file, index) => {
        if (file) {
          documents.push({
            ...file,
            typeLabel: `${documentTypeLabels[key] || key}${
              fileArray.length > 1 ? ` (${index + 1})` : ""
            }`,
            key: `${key}-${index}`,
            source: "submission" as const,
            uploadedBy: getUserName(
              file.uploadedBy as
                | { firstName?: string; lastName?: string }
                | string
                | null
            ),
          });
        }
      });
    } else {
      // Handle single files
      if (doc) {
        documents.push({
          ...doc,
          typeLabel: documentTypeLabels[key] || key,
          key,
          source: "submission" as const,
          uploadedBy: getUserName(
            doc.uploadedBy as
              | { firstName?: string; lastName?: string }
              | string
              | null
          ),
        });
      }
    }
  });

  // Add additional documents
  const additionalDocuments = (project?.additionalDocuments || []).map(
    (doc) => ({
      _id: doc._id,
      name: doc.name,
      url: doc.url,
      size: doc.size,
      typeLabel: doc.tag,
      key: doc._id,
      uploadedAt: doc.uploadedAt,
      uploadedBy: getUserName(doc.uploadedBy),
      source: "additional" as const,
    })
  );

  // Add AHJ documents
  const ahjDocuments = (project?.ahjDocuments || []).map((doc: IAHJDocument) => ({
    _id: doc._id,
    name: doc.name,
    url: doc.url,
    size: doc.size,
    typeLabel:
      permittingRequirementsLabels[doc.requirementKey] || doc.requirementKey,
    key: doc._id,
    uploadedAt: doc.uploadedAt,
    uploadedBy: getUserName(doc.uploadedBy),
    source: "ahj" as const,
  }));

  const allDocuments = [...documents, ...additionalDocuments, ...ahjDocuments];

  const handleDocumentAdded = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    const doc = allDocuments.find((d) => d.key === documentId);
    if (!doc) return;

    try {
      setDeletingDocument(documentId);
      if (doc.source === "ahj") {
        await projectApi.deleteAHJDocument(projectId, documentId);
      } else {
        await projectApi.deleteAdditionalDocument(projectId, documentId);
      }

      toast({
        title: "Document deleted",
        description: "The document has been deleted successfully",
      });

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Delete failed",
        description: "Failed to delete document",
        variant: "destructive",
      });
    } finally {
      setDeletingDocument(null);
    }
  };

  const canDeleteDocument = (document: {
    source: string;
    uploadedBy?: string;
  }) => {
    if (document.source === "submission") return false; // Can't delete submission documents
    if (!canPerformActions) return false; // Members can't delete anything
    // Allow deleting AHJ documents if admin or uploader
    if (document.source === "ahj") {
      return (
        user?.role === "Admin" ||
        (document.uploadedBy &&
          document.uploadedBy.includes(user?.firstName || "") &&
          document.uploadedBy.includes(user?.lastName || ""))
      );
    }
    return (
      user?.role === "Admin" ||
      (document.uploadedBy &&
        document.uploadedBy.includes(user?.firstName || "") &&
        document.uploadedBy.includes(user?.lastName || ""))
    );
  };

  const handleDownloadAll = async () => {
    try {
      setDownloading(true);
      const blob = await projectApi.downloadProjectFiles(projectId);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `project_${projectId}_documents.zip`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download project files", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="shadow-sophisticated border-none">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-border/50 pb-4">
        <CardTitle className="text-lg font-semibold">Documents</CardTitle>
        <div className="flex gap-2 mt-4 sm:mt-0">
          {canPerformActions && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsModalOpen(true)}
              className="shadow-sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Documents
            </Button>
          )}
          {allDocuments.length > 0 && (
            <Button
              size="sm"
              onClick={handleDownloadAll}
              disabled={downloading}
              className="shadow-sm"
            >
              {downloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {downloading ? "Preparing..." : "Download All"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className={isMobile ? "p-0" : "p-6"}>
        {allDocuments.length === 0 ? (
          <div
            className={`text-center text-muted-foreground ${
              isMobile ? "py-8 px-4" : "py-12"
            }`}
          >
            <div className="bg-muted/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Paperclip className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-1">No documents yet</h3>
            <p className="text-sm">Upload documents to see them here.</p>
          </div>
        ) : (
          <div className={isMobile ? "divide-y" : "space-y-3"}>
            {allDocuments.map((doc, index) => (
              <div
                key={doc.key}
                className={`flex items-start gap-4 transition-all group ${
                  isMobile
                    ? "p-4 hover:bg-muted/30"
                    : "p-4 hover:bg-muted/30 rounded-xl border border-border/50 hover:border-primary/20 hover:shadow-sm"
                }`}
              >
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div className="bg-primary/10 p-2.5 rounded-lg text-primary group-hover:scale-105 transition-transform">
                    {getFileIcon(doc.name)}
                  </div>
                  {isMobile && (
                    <span className="text-[10px] text-muted-foreground text-center leading-tight mt-1">
                      {formatFileSize(doc.size)}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <p
                          className={`font-medium truncate text-foreground ${
                            isMobile ? "text-sm" : "text-base"
                          }`}
                          title={doc.name}
                        >
                          {doc.name}
                        </p>
                        <Badge
                          variant={
                            doc.source === "additional"
                              ? "default"
                              : "secondary"
                          }
                          className={`shrink-0 font-normal ${
                            isMobile
                              ? "text-[10px] px-1.5 py-0 h-5"
                              : "text-xs"
                          }`}
                        >
                          {doc.typeLabel}
                        </Badge>
                      </div>
                      
                      {!isMobile && (
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                            {formatFileSize(doc.size)}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                            Uploaded by {doc.uploadedBy}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                            {formatDate(doc.uploadedAt)}
                          </span>
                        </div>
                      )}
                      
                      {isMobile && (
                        <div className="flex flex-col gap-0.5 mt-1">
                          <span className="text-[11px] text-muted-foreground truncate">
                            By {doc.uploadedBy}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {format(new Date(doc.uploadedAt), "MMM d, yyyy")}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className={isMobile ? "h-8 w-8 p-0" : "h-8 w-8 p-0 hover:text-primary hover:bg-primary/10"}
                        title="Download"
                      >
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download
                            className={`${isMobile ? "h-4 w-4" : "h-4 w-4"}`}
                          />
                          <span className="sr-only">Download</span>
                        </a>
                      </Button>
                      {canDeleteDocument(doc) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.key)}
                          disabled={deletingDocument === doc.key}
                          className={`text-muted-foreground hover:text-destructive hover:bg-destructive/10 ${
                            isMobile ? "h-8 w-8 p-0" : "h-8 w-8 p-0"
                          }`}
                          title="Delete"
                        >
                          {deletingDocument === doc.key ? (
                            <Loader2
                              className={`${
                                isMobile ? "h-4 w-4" : "h-4 w-4"
                              } animate-spin`}
                            />
                          ) : (
                            <Trash2
                              className={`${isMobile ? "h-4 w-4" : "h-4 w-4"}`}
                            />
                          )}
                          <span className="sr-only">Delete</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AddAdditionalDocumentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projectId={projectId}
        onDocumentAdded={handleDocumentAdded}
      />
    </Card>
  );
};

export default SubmissionDocumentsTab;
